import { useState, useCallback, useEffect } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { TimekeeperService } from 'services/TimekeeperService';
import { EmployeeService } from 'services/EmployeeService';
import type { PayRuleDB } from 'repository/timekeeper/@types/PayRuleModel';

const timekeeperService = new TimekeeperService();
const employeeService = new EmployeeService();

export type PayRuleRow = {
    id: number;
    employee_r_pay_rule_id: number | null;
    employee_id: number;
    pay_rule_id: number | '';
    start_date: Dayjs;
    end_date: Dayjs;
};

export type PayRuleFormValues = {
    pay_rules: PayRuleRow[];
};

type UsePayRuleDataProps = {
    employeeId: number;
    open: boolean;
    onLoaded: (rows: PayRuleRow[]) => void;
};

export const usePayRuleData = ({ employeeId, open, onLoaded }: UsePayRuleDataProps) => {
    const [availablePayRules, setAvailablePayRules] = useState<PayRuleDB[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const loadData = useCallback(async () => {
        if (!employeeId) return;
        setLoading(true);
        try {
            const [existingResult, allPayRulesResult] = await Promise.allSettled([
                employeeService.employee.getEmployeePayRules(employeeId),
                timekeeperService.payrules.getPayRules(),
            ]);

            if (allPayRulesResult.status === 'fulfilled') {
                setAvailablePayRules(allPayRulesResult.value);
            }

            if (existingResult.status === 'fulfilled') {
                const existing = existingResult.value || [];
                const rows: PayRuleRow[] = existing.map((p, i) => ({
                    id: i + 1,
                    employee_r_pay_rule_id: p.employee_r_pay_rule_id,
                    employee_id: p.employee_id,
                    pay_rule_id: p.pay_rule_id,
                    start_date: dayjs(p.start_date),
                    end_date: dayjs(p.end_date),
                }));
                onLoaded(rows);
            }
        } catch (error) {
            console.error('Error loading pay rules:', error);
        } finally {
            setLoading(false);
        }
    }, [employeeId, onLoaded]);

    useEffect(() => {
        if (open && employeeId) loadData();
    }, [open, employeeId, loadData]);

    return { availablePayRules, loading };
};
